import { useCallback, useEffect, useState } from "react";
import { rtdb } from "../../services/firebase";
import { ChatMessage } from "../../types/db/chat";
import { ChatMessageService } from "../../types/services/chat";

const chatMessageService = new ChatMessageService(rtdb);

/** Message info is the info of the message within channel with the message id */
type MessageInfo = ChatMessage & { id: string };

/** The default limit of messages to fetch */
const DEFAULT_LIMIT_TO_LAST = 50;

/**
 * Hook which takes care of fetching and updating the list of message in the thread
 * @param channelId The channel ID of the channel
 * @returns the array of messages in the thread
 */
export const useChatMessages = (channelId: string): {
    /** The messages in the thread */
    messages: MessageInfo[];
    /** If any action is currently loading */
    isLoading?: boolean;
    /** If any action ended up with an error */
    error?: string;
    /** Function to create/send a new message. Promise returning the message id */
    sendMessage(sender: string, message: string): Promise<string>;
    /** Function to update a message */
    editMessage(messageId: string, message: string): Promise<void>;
    /** Function to delete a message */
    deleteMessage(messageId: string): Promise<void>;
    /** Function to load more messages (pagination) */
    loadMoreMessages(): Promise<void>;
} => {
    const [isLoading, setIsLoading] = useState(true);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [error, setError] = useState('');
    const [messages, setMessages] = useState<MessageInfo[]>([]);

    useEffect(() => {
        //fetch the conversation in the thread
        const fetchThread = chatMessageService.getParentRef(channelId);

        //subscribe to any added child of that conversation
        fetchThread.limitToLast(DEFAULT_LIMIT_TO_LAST)
            .on("child_added", (snapshot) => {
                const messageInfo: ChatMessage = snapshot.val();
                const messageID = snapshot.key!;
                setIsLoading(false);
                // console.log('child_added', messageID, messageInfo)
                // append the message on the page
                setMessages(currentItems => [
                    ...currentItems,
                    {
                        id: messageID,
                        ...messageInfo,
                    }
                ]);
            });

        fetchThread.on("child_changed", (snapshot) => {
            const messageInfo: ChatMessage = snapshot.val();
            const messageID = snapshot.key!;
            //console.log('child_changed', messageID, messageInfo)
            // modify the item on the list
            setMessages(currentItems => {
                const index = currentItems.findIndex(item => item.id === messageID);
                if (index === -1) {
                    //if not found in the list, return as is
                    return currentItems;
                }

                return [
                    ...currentItems.slice(0, index),
                    {
                        id: messageID,
                        ...messageInfo,
                    },
                    ...currentItems.slice(index + 1),
                ]
            });
        })

        fetchThread.on("child_removed", (snapshot) => {
            // const messageInfo: ChatMessage = snapshot.val();
            const messageID = snapshot.key!;
            // console.log('child_removed', messageID, messageInfo)
            //remove the channel from the channel list
            setMessages(currentItems =>
                currentItems.filter(item => item.id !== messageID)
            );
        })

        return () => {
            fetchThread.off();//disconnect the existing listener
            setMessages([]);//reset the message list (channelID changed)
        };
    }, [channelId]);

    useEffect(() => {
        //from the nature of the subscription, it is possible that the user doesn't have channels
        //so `setIsLoading(false)` never called, therefore we add a timeout
        setTimeout(() => setIsLoading(false), 1000)
    }, []);


    const sendMessage = useCallback((sender: string, message: string) => {
        return chatMessageService.create(
            channelId,
            {
                type: 'text',
                message,
                sender,
            })
    }, [channelId]);

    const editMessage = useCallback((messageId: string, message: string) => {
        return chatMessageService.update(
            channelId,
            messageId,
            {
                message,
            })
    }, [channelId]);

    const deleteMessage = useCallback((messageId: string) => {
        return chatMessageService.delete(
            channelId,
            messageId)
    }, [channelId]);

    const loadMoreMessages = useCallback(async () => {
        //TODO
    }, []);

    return {
        messages,
        isLoading,
        error,
        loadMoreMessages,
        sendMessage,
        editMessage,
        deleteMessage,
    };
}
